'use client';

import { getRuntimeReleaseStateSnapshot, subscribeRuntimeReleaseState } from '@/client/runtime-release-store';
import { AUTH_TOKEN_PARAM } from '@/constants/auth';
import { useAuth } from '@/context/auth.context';
import { overlayLayers } from '@/ui/overlay-layers';
import clsx from 'clsx';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

const formatRevisionShort = (revision: string) => revision.slice(0, 12);

const reloadCurrentPageWithAccessToken = (accessToken: string) => {
    if (accessToken) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set(AUTH_TOKEN_PARAM, accessToken);
        const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
        window.history.replaceState(window.history.state, '', nextUrl);
    }

    window.location.reload();
};

export const RuntimeReleaseMonitor = () => {
    const { accessToken } = useAuth();
    const runtimeReleaseState = useSyncExternalStore(
        subscribeRuntimeReleaseState,
        getRuntimeReleaseStateSnapshot,
        getRuntimeReleaseStateSnapshot,
    );
    const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
    const [dismissedRevision, setDismissedRevision] = useState<string | null>(null);

    useEffect(() => {
        setPortalTarget(document.body);
    }, []);

    if (!portalTarget) {
        return null;
    }

    const shouldRenderSoftBanner =
        runtimeReleaseState.status === 'refresh-available' &&
        dismissedRevision !== runtimeReleaseState.backendManifest.revision;

    const shouldRenderHardStop = runtimeReleaseState.status === 'reload-required';

    if (!shouldRenderSoftBanner && !shouldRenderHardStop) {
        return null;
    }

    return createPortal(
        <>
            {shouldRenderSoftBanner && (
                <div
                    className="pointer-events-none fixed inset-x-0 top-0"
                    style={{ zIndex: overlayLayers.overlayRuntimeBanner }}
                >
                    <div
                        className={clsx(
                            'pointer-events-auto flex min-h-14 items-center justify-between gap-4 border-b border-amber-200 bg-amber-50 px-4 py-3 shadow-sm',
                            'sm:px-6',
                        )}
                        role="status"
                        aria-live="polite"
                    >
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-amber-950">Доступно обновление сервиса</p>
                            <p className="mt-1 text-xs text-amber-900">
                                Можно продолжить работу, но лучше обновить страницу.
                            </p>
                            <p className="mt-1 text-xs text-amber-800">
                                Версия сервера: {formatRevisionShort(runtimeReleaseState.backendManifest.revision)}
                            </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <button
                                type="button"
                                className="rounded-md bg-amber-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-amber-950"
                                onClick={() => reloadCurrentPageWithAccessToken(accessToken)}
                            >
                                Обновить
                            </button>
                            <button
                                type="button"
                                className="rounded-md border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-900 transition hover:bg-amber-100"
                                onClick={() => setDismissedRevision(runtimeReleaseState.backendManifest.revision)}
                            >
                                Позже
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {shouldRenderHardStop && (
                <div
                    className="fixed inset-0 flex items-center justify-center bg-black/70 p-4"
                    style={{ zIndex: overlayLayers.overlayRuntimeHardStopBackdrop }}
                >
                    <div
                        className="w-full max-w-lg rounded-2xl border border-rose-200 bg-white p-6 shadow-2xl"
                        style={{ zIndex: overlayLayers.overlayRuntimeHardStopContent }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="runtime-release-hard-stop-title"
                    >
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-700">
                                    СОВМЕСТИМОСТЬ
                                </p>
                                <h2
                                    id="runtime-release-hard-stop-title"
                                    className="text-xl font-semibold text-slate-950"
                                >
                                    Нужно перезагрузить страницу
                                </h2>
                                <p className="text-sm leading-6 text-slate-700">
                                    Эта вкладка больше не совместима с текущей версией сервиса.
                                </p>
                            </div>

                            <div className="rounded-xl bg-rose-50 px-4 py-3 text-xs text-rose-900">
                                <p>
                                    Версия интерфейса: {formatRevisionShort(runtimeReleaseState.bootManifest.revision)}
                                </p>
                                <p className="mt-1">
                                    Версия сервера: {formatRevisionShort(runtimeReleaseState.backendManifest.revision)}
                                </p>
                            </div>

                            <button
                                type="button"
                                className="w-full rounded-lg bg-rose-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-rose-800"
                                onClick={() => reloadCurrentPageWithAccessToken(accessToken)}
                            >
                                Перезагрузить страницу
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>,
        portalTarget,
    );
};
